import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, posterUrl } from "@/lib/api";
import { GraduationCap, Play } from "lucide-react";

// Horizontal "Learn" shelf surfacing the latest @matrixguide tutorials.
// Used as an onboarding funnel on the empty-feed and end-of-feed screens.
// Renders nothing when the agent hasn't published anything yet.
export const GuideLearnShelf = ({ heading = "Learn MatrixShopping", limit = 8, className = "" }) => {
  const [tutorials, setTutorials] = useState([]);
  const [account, setAccount] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/guide/tutorials", { params: { limit } });
        if (!alive) return;
        setTutorials(Array.isArray(r.data?.tutorials) ? r.data.tutorials : []);
        setAccount(r.data?.account || null);
      } catch {
        /* silent — shelf just won't render */
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [limit]);

  if (!loaded || tutorials.length === 0) return null;

  return (
    <div data-testid="guide-learn-shelf" className={className}>
      <div className="flex items-center gap-2 mb-3">
        <GraduationCap className="h-4 w-4 text-[#FFD700]" strokeWidth={2} />
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FFD700]">
          {heading}
        </p>
        {account?.username && (
          <Link
            to={`/u/${account.username}`}
            data-testid="guide-learn-account"
            className="font-mono text-[10px] text-white/40 hover:text-[#00FFFF] transition-colors"
          >
            @{account.username}
          </Link>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
        {tutorials.map((t) => (
          <Link
            key={t.id}
            to={`/v/${t.id}`}
            data-testid={`guide-learn-card-${t.id}`}
            className="group relative shrink-0 w-28 sm:w-32 border border-white/10 bg-black overflow-hidden hover:border-[#FFD700]/60 transition-colors"
          >
            <div className="relative aspect-[9/16] bg-white/5">
              <img
                src={posterUrl(t.id)}
                alt={t.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="h-9 w-9 rounded-full bg-[#FFD700]/90 flex items-center justify-center">
                  <Play className="h-4 w-4 text-black fill-black" />
                </div>
              </div>
              <p className="absolute bottom-0 inset-x-0 p-1.5 text-[10px] leading-tight text-white font-medium line-clamp-2">
                {t.title}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default GuideLearnShelf;
